import { CommonModule } from '@angular/common';
import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';

import {
  UiStepperState,
  UiStepperStep,
  UiStepperSubStep,
} from './stepper.types';
import { StepperMainStepComponent } from './stepper-main-step.component';
import { StepperSubStepComponent } from './stepper-sub-step.component';

interface ResolvedUiStepperSubStep extends UiStepperSubStep {
  state: UiStepperState;
}

interface ResolvedUiStepperStep extends UiStepperStep {
  state: UiStepperState;
  subSteps: ResolvedUiStepperSubStep[];
}

@Component({
  selector: 'app-stepper',
  standalone: true,
  imports: [CommonModule, StepperMainStepComponent, StepperSubStepComponent],
  templateUrl: './stepper.component.html',
  styleUrl: './stepper.component.css',
})
export class StepperComponent implements OnChanges {
  @Input() steps: UiStepperStep[] = [];

  @Input() activeStepId?: string | number;

  resolvedSteps: ResolvedUiStepperStep[] = [];

  get columnsTemplate(): string {
    const count = this.resolvedSteps.length || 1;
    return `repeat(${count}, minmax(185px, 1fr))`;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ('steps' in changes || 'activeStepId' in changes) {
      this.resolveSteps();
    }
  }

  trackByStep(index: number, step: UiStepperStep): string | number {
    return step.id ?? index;
  }

  trackBySubStep(index: number, subStep: UiStepperSubStep): string | number {
    return subStep.id ?? index;
  }

  isConnectorActive(stepIndex: number): boolean {
    if (stepIndex >= this.resolvedSteps.length - 1) {
      return false;
    }

    const activeIndex = this.resolveActiveStepIndex();
    if (activeIndex !== -1) {
      return stepIndex <= activeIndex;
    }

    const currentState = this.resolvedSteps[stepIndex].state;
    return currentState === 'completed' || currentState === 'active';
  }

  private resolveActiveStepIndex(): number {
    if (this.activeStepId != null) {
      const indexFromId = this.resolvedSteps.findIndex(
        (step) => step.id === this.activeStepId
      );
      if (indexFromId !== -1) {
        return indexFromId;
      }
    }

    return this.resolvedSteps.findIndex(
      (step) => step.state === 'active' || step.state === 'error'
    );
  }

  private resolveSteps(): void {
    const activeIndex = this.steps.findIndex(
      (step) => step.id === this.activeStepId
    );

    this.resolvedSteps = this.steps.map((step, stepIndex) => {
      const fallbackState = this.resolveStepStateFromIndex(
        stepIndex,
        activeIndex
      );
      const stepState = step.state ?? fallbackState;
      const resolvedSubSteps = (step.subSteps ?? []).map((subStep) => ({
        ...subStep,
        state: subStep.state ?? 'pending',
      }));

      return {
        ...step,
        state: stepState,
        subSteps: resolvedSubSteps,
      };
    });
  }

  private resolveStepStateFromIndex(
    stepIndex: number,
    activeIndex: number
  ): UiStepperState {
    if (activeIndex === -1) {
      return 'pending';
    }

    if (stepIndex < activeIndex) {
      return 'completed';
    }

    if (stepIndex === activeIndex) {
      return 'active';
    }

    return 'pending';
  }
}
